// File: src/contexts/AuthContext.js
import React, { createContext, useState, useEffect } from 'react';
import apiClient from '../api/apiClient'; 
// ^ Ensure you have an axios/fetch wrapper or replace with fetch calls

// Create the AuthContext
export const AuthContext = createContext();

const MAX_AUTH_RETRIES = 3;
const RETRY_DELAY = 1000;

/**
 * AuthProvider component:
 * - Holds auth-related state (token).
 * - Provides login, register, logout methods.
 * - Persists token in localStorage.
 */
export function AuthProvider({ children }) {
  const [token, setToken] = useState(() => localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [retryCount, setRetryCount] = useState(0);

  const validateToken = async () => {
    if (!token) return false;
    try {
      const response = await apiClient.get('/auth/validate', {
        headers: { Authorization: `Bearer ${token}` }
      });
      return response.data.isValid;
    } catch (error) {
      return false;
    }
  };

  useEffect(() => {
    const attemptValidation = async () => {
      if (!token) {
        setLoading(false);
        return;
      }

      const isValid = await validateToken();
      if (isValid) {
        setIsAuthenticated(true);
        apiClient.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        setLoading(false);
      } else if (retryCount < MAX_AUTH_RETRIES) {
        console.log(`Retrying auth validation... Attempt ${retryCount + 1}/${MAX_AUTH_RETRIES}`);
        setTimeout(() => setRetryCount(prev => prev + 1), RETRY_DELAY);
      } else {
        handleLogout();
        setLoading(false);
      }
    };

    if (loading) {
      attemptValidation();
    }
  }, [token, retryCount, loading]);

  console.log(`User Authenticated: ${isAuthenticated}`)

  // Login method
  async function login(email, password) {
    const response = await apiClient.post('/auth/login', {
      email,
      password,
    });
    const { access_token } = response.data;
    setToken(access_token);
    localStorage.setItem('token', access_token);

    // Store email in local storage
    localStorage.setItem('userEmail', email);
    apiClient.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    setIsAuthenticated(true);
  }

  // Register method
  async function register(email, password) {
    await apiClient.post('/auth/register', {
      email,
      password,
    });
    // Optionally, you can auto-login or redirect to the login page here
  }

  // Logout method
  function handleLogout() {
    setToken(null);
    setIsAuthenticated(false);
    localStorage.clear(); // Clear all localStorage
    delete apiClient.defaults.headers.common['Authorization'];
  }

  // Handle Google OAuth callback
  async function handleGoogleCallback(token) {
    setToken(token);
    localStorage.setItem('token', token);
    apiClient.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    setIsAuthenticated(true);

    try {
      // Use the token to get user profile data
      const response = await apiClient.get('/auth/user-profile', {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Store user email and picture in localStorage
      if (response.data.email) {
        localStorage.setItem('userEmail', response.data.email);
      }
      
      if (response.data.picture) {
        localStorage.setItem('userPicture', response.data.picture);
      }
    } catch (error) {
      console.error('Error fetching user data:', error);
    }
  }

  // AuthContext value
  const contextValue = {
    token,
    isAuthenticated,
    login,
    register,
    logout: handleLogout,
    loading,
    handleGoogleCallback
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
}
